/**
 * ShopFilterPanel — world-class filter navigation for /shop
 * ---------------------------------------------------------
 * Mirrors the design language of the primary app Sidebar (see
 * /app/frontend/src/components/layout/Sidebar.tsx and
 * /app/design_guidelines.json):
 *   • Sticky section headers with uppercase tracking-[0.18em] labels
 *   • Section-color icon coding (sky / emerald / fuchsia / saffron / slate)
 *   • Active-row saffron treatment (2px left-border, tinted bg, tinted text)
 *   • Compact row rhythm (py-1, 13px labels) for max information density
 *   • Radix Tooltip hover explanations for each filter section
 *   • Global filter-search input with ⌘F / "/" keyboard focus shortcut
 *   • Selected-count badges next to section headers
 *   • Sticky footer with `Shift+C` clear shortcut and a keyboard-hint line
 *
 * Keyboard shortcuts (registered globally when the panel is mounted):
 *   • ⌘F or "/"    → focus the filter-search input (or brand search)
 *   • ⇧C           → clear all filters (only fires when > 0 active)
 *   • Esc          → clear the filter-search box
 *
 * The mobile drawer is the same panel, wrapped in a bottom-sheet dialog
 * so the look-and-feel is identical between desktop and mobile.
 */
import React from "react";
import {
  Filter, Globe, ShieldCheck, FolderTree, IndianRupee, Building2,
  Star, Sparkles, Search, X as XIcon, ChevronRight,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { CountryFilter } from "@/components/shop/CountryFilter";
import { GradientRangeSlider } from "@/components/shop/GradientRangeSlider";
import { VerifiedSellerBadge } from "@/components/shop/VerifiedSellerBadge";
import type { CategoryNode } from "@/lib/types";

export type ConditionKey = "any" | "new" | "used" | "refurbished";

// Section-color palette — mirrors the primary Sidebar's SECTION_ICON_COLOR
// so the two navigation surfaces feel like one product.
const SECTION_TINTS = {
  sky:     { icon: "text-sky-500",                bar: "bg-sky-500" },
  emerald: { icon: "text-emerald-500",            bar: "bg-emerald-500" },
  fuchsia: { icon: "text-fuchsia-500",            bar: "bg-fuchsia-500" },
  saffron: { icon: "text-[color:var(--sb-saffron,#F97316)]", bar: "bg-[color:var(--sb-saffron,#F97316)]" },
  slate:   { icon: "text-slate-500 dark:text-slate-400", bar: "bg-slate-500" },
} as const;

type Tint = keyof typeof SECTION_TINTS;

/* ------------------------------------------------------------- */
/* SectionHeader — sticky, colored, with optional count + tooltip */
/* ------------------------------------------------------------- */
const SectionHeader: React.FC<{
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  tint: Tint;
  count?: number;
  hint?: string;
  right?: React.ReactNode;
  testId?: string;
}> = ({ icon: Icon, label, tint, count, hint, right, testId }) => {
  const { icon } = SECTION_TINTS[tint];
  const header = (
    <div
      data-testid={testId}
      className="sticky top-0 z-10 -mx-1 px-1 py-1.5 mb-2 bg-[color:var(--sg-card)]/95 backdrop-blur-sm flex items-center gap-2"
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 ${icon}`} strokeWidth={2.25} />
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[color:var(--sg-muted)]">
        {label}
      </span>
      {count !== undefined && count > 0 && (
        <span
          data-testid={`${testId}-count`}
          className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[9px] font-bold rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white tabular-nums"
        >
          {count}
        </span>
      )}
      <span className="flex-1" />
      {right}
    </div>
  );
  if (!hint) return header;
  return (
    <Tooltip delayDuration={250}>
      <TooltipTrigger asChild>{header}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="max-w-[220px] text-xs">
        {hint}
      </TooltipContent>
    </Tooltip>
  );
};

/* --------------------------- */
/* FilterRow — checkbox / radio */
/* --------------------------- */
const FilterRow: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  testId: string;
  kind?: "checkbox" | "radio";
}> = ({ active, onClick, label, count, testId, kind = "checkbox" }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`sg-nav-link relative w-full flex items-center gap-2.5 pl-4 pr-3 py-1.5 rounded-md text-[13px] font-medium leading-tight transition-colors text-left ${
        active
          ? "bg-[color:var(--sb-saffron,#F97316)]/10 text-[color:var(--sb-saffron,#F97316)] font-semibold before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[2px] before:rounded-r before:bg-[color:var(--sb-saffron,#F97316)]"
          : "text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)]"
      }`}
    >
      <span
        aria-hidden
        className={`shrink-0 h-3.5 w-3.5 flex items-center justify-center border transition-all ${
          kind === "radio" ? "rounded-full" : "rounded-[3px]"
        } ${
          active
            ? "border-[color:var(--sb-saffron,#F97316)] bg-[color:var(--sb-saffron,#F97316)]"
            : "border-[color:var(--sg-border)] bg-transparent"
        }`}
      >
        {active && kind === "checkbox" && (
          <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
            <path d="M2 6l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {active && kind === "radio" && (
          <span className="h-1.5 w-1.5 rounded-full bg-white" />
        )}
      </span>
      <span className="flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="text-[10px] tabular-nums text-[color:var(--sg-muted)]">{count}</span>
      )}
    </button>
  );
};

/* ------------------------------------------------------ */
/* Public props — matches the state Shop.tsx already holds */
/* ------------------------------------------------------ */
export interface ShopFilterPanelProps {
  // Data
  categories: CategoryNode[];
  brands: string[];
  // State
  category: string | null;
  brand: string | null;
  country: string | null;
  verifiedOnly: boolean;
  priceRange: [number, number];
  scoreRange: [number, number];
  condition: ConditionKey;
  // Setters
  onSetCategory: (v: string | null) => void;
  onSetBrand: (v: string | null) => void;
  onSetCountry: (v: string | null) => void;
  onSetVerifiedOnly: (v: boolean) => void;
  onSetPriceRange: (r: [number, number]) => void;
  onSetScoreRange: (r: [number, number]) => void;
  onSetCondition: (c: ConditionKey) => void;
  onClearAll: () => void;
  // Currency
  formatPrice: (v: number, opts?: any) => string;
  currencyCode: string;
  // Meta
  activeFilterCount: number;
  /** Ref-forwarded to the panel scroll container so the parent can autoscroll on active filter changes. */
  scrollRef?: React.Ref<HTMLDivElement>;
}

export const ShopFilterPanel: React.FC<ShopFilterPanelProps> = (props) => {
  const {
    categories, brands,
    category, brand, country, verifiedOnly, priceRange, scoreRange, condition,
    onSetCategory, onSetBrand, onSetCountry, onSetVerifiedOnly,
    onSetPriceRange, onSetScoreRange, onSetCondition, onClearAll,
    formatPrice, currencyCode,
    activeFilterCount,
    scrollRef,
  } = props;

  const [globalSearch, setGlobalSearch] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  // Filter brands + categories via the global search box.
  const q = globalSearch.trim().toLowerCase();
  const filteredCategories = q
    ? categories.filter((c) => c.name.toLowerCase().includes(q))
    : categories;
  const filteredBrands = q
    ? brands.filter((b) => b.toLowerCase().includes(q))
    : brands;

  // Selected counts per section — powers header count-badges.
  const priceDirty = priceRange[0] > 0 || priceRange[1] < 200000;
  const scoreDirty = scoreRange[0] > 0 || scoreRange[1] < 1000;
  const conditionDirty = condition !== "any";
  const categoryCount = category ? 1 : 0;
  const brandCount = brand ? 1 : 0;
  const verifiedCount = verifiedOnly ? 1 : 0;
  const countryCount = country ? 1 : 0;

  /* ----------------------------- */
  /* Global keyboard shortcuts     */
  /* ----------------------------- */
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      const isInputContext =
        tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable;

      // The one modal whose keyboard we must NOT hijack is the global
      // CommandPalette (⌘K). Everything else — cookie banners, consent
      // dialogs, promo banners — is fine to ignore. We detect the cmdk
      // by its input testid which only exists while the palette is open.
      const cmdkOpen =
        typeof document !== "undefined" &&
        !!document.querySelector('[data-testid="cmdk-input"]');

      // ⌘F / Ctrl+F → focus filter-search. We deliberately override the
      // browser's find dialog because "find in filters" is more useful
      // on the shop page than "find text on page".
      if ((e.metaKey || e.ctrlKey) && (e.key === "f" || e.key === "F")) {
        if (cmdkOpen) return;
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        return;
      }
      // Shift+C → clear all — only when active filters exist, user isn't
      // typing, and cmdk isn't consuming keystrokes.
      if (e.shiftKey && (e.key === "c" || e.key === "C") && !isInputContext && !cmdkOpen) {
        if (activeFilterCount > 0) {
          e.preventDefault();
          onClearAll();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeFilterCount, onClearAll]);

  return (
    <TooltipProvider delayDuration={250}>
      <div className="flex flex-col h-full min-h-0" data-testid="shop-filter-panel">
        {/* Header — Filters title + clear pill */}
        <div className="shrink-0 px-4 pt-4 pb-3 border-b border-[color:var(--sg-border)] flex items-center gap-2">
          <Filter className="h-4 w-4 text-[color:var(--sb-saffron,#F97316)]" strokeWidth={2.25} />
          <div className="font-display font-bold text-sm tracking-tight text-[color:var(--sg-fg)]">
            Filters
          </div>
          {activeFilterCount > 0 && (
            <span
              data-testid="shop-filter-active-count"
              className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-bold rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white tabular-nums"
            >
              {activeFilterCount}
            </span>
          )}
          <span className="flex-1" />
          {activeFilterCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onClearAll}
                  data-testid="shop-filter-clear"
                  className="text-[11px] text-[color:var(--sb-saffron,#F97316)] hover:text-[color:var(--sb-saffron,#F97316)]/80 font-display font-semibold inline-flex items-center gap-1"
                >
                  Clear
                  <kbd className="hidden md:inline-block text-[9px] font-mono bg-[color:var(--sg-muted-bg)] border border-[color:var(--sg-border)] rounded px-1 py-0.5">
                    ⇧C
                  </kbd>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Clear all filters ({activeFilterCount})
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Global filter-search — focuses on ⌘F or "/" */}
        <div className="shrink-0 px-3 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--sg-muted)]" strokeWidth={2} />
            <input
              ref={searchInputRef}
              type="search"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") { e.preventDefault(); setGlobalSearch(""); (e.target as HTMLInputElement).blur(); }
              }}
              placeholder="Search filters…"
              data-testid="shop-filter-global-search"
              aria-label="Search filters"
              className="w-full h-8 pl-8 pr-14 text-[12px] rounded-md bg-[color:var(--sg-muted-bg)]/60 border border-[color:var(--sg-border)] text-[color:var(--sg-fg)] placeholder:text-[color:var(--sg-muted)] focus:bg-[color:var(--sg-card)] focus:border-[color:var(--sb-saffron,#F97316)]/50 focus:outline-none transition-colors"
            />
            {globalSearch ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => { setGlobalSearch(""); searchInputRef.current?.focus(); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] rounded"
              >
                <XIcon className="h-3 w-3" />
              </button>
            ) : (
              <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-mono bg-[color:var(--sg-card)] border border-[color:var(--sg-border)] rounded px-1 py-0.5 text-[color:var(--sg-muted)]">
                {isMac ? "⌘F" : "Ctrl+F"}
              </kbd>
            )}
          </div>
        </div>

        {/* Filter body — flows naturally, NO internal scroll. Every filter
            section is visible at once; the enclosing aside grows to fit. */}
        <div
          ref={scrollRef}
          data-testid="shop-filter-scroll"
          className="px-2 pb-4"
        >
          {/* SHIPS FROM */}
          <div className="pt-3">
            <SectionHeader
              icon={Globe}
              label="Ships from"
              tint="sky"
              count={countryCount}
              hint="Filter listings by the country a seller ships from."
              testId="shop-filter-section-country"
            />
            <div className="px-1 pb-3">
              <CountryFilter value={country} onChange={onSetCountry} />
            </div>
          </div>

          {/* TRUST */}
          <div>
            <SectionHeader
              icon={ShieldCheck}
              label="Trust"
              tint="emerald"
              count={verifiedCount}
              hint="KYC + GST-verified brands only. Every verified brand carries the SociBaba trust seal."
              testId="shop-filter-section-trust"
            />
            <div className="px-1 pb-3">
              <label
                className={`flex items-center gap-3 cursor-pointer select-none px-3 py-2 rounded-md border transition-colors ${
                  verifiedOnly
                    ? "border-emerald-500/60 bg-emerald-500/5"
                    : "border-[color:var(--sg-border)] bg-[color:var(--sg-card)] hover:border-emerald-500/60"
                }`}
                data-testid="shop-filter-verified"
              >
                <input
                  type="checkbox"
                  checked={verifiedOnly}
                  onChange={(e) => onSetVerifiedOnly(e.target.checked)}
                  className="accent-emerald-500 h-4 w-4"
                  data-testid="shop-filter-verified-checkbox"
                />
                <span className="flex-1 text-[13px] font-medium text-[color:var(--sg-fg)]">
                  Verified brands only
                </span>
                <VerifiedSellerBadge verified size="xs" showLabel={false} />
              </label>
            </div>
          </div>

          {/* CATEGORY */}
          <div>
            <SectionHeader
              icon={FolderTree}
              label="Category"
              tint="fuchsia"
              count={categoryCount}
              hint="Pick a single category to narrow the grid. Click again to deselect."
              testId="shop-filter-section-category"
            />
            <div className="px-1 pb-3 space-y-0.5 pr-1" data-testid="shop-filter-category-list">
              <FilterRow
                active={category === null}
                onClick={() => onSetCategory(null)}
                label="All categories"
                testId="shop-filter-cat-all"
                kind="radio"
              />
              {filteredCategories.map((c) => (
                <FilterRow
                  key={c.id}
                  active={category === c.id}
                  onClick={() => onSetCategory(category === c.id ? null : c.id)}
                  label={c.name}
                  testId={`shop-filter-cat-${c.id}`}
                />
              ))}
              {filteredCategories.length === 0 && (
                <div className="text-[11px] text-[color:var(--sg-muted)] px-3 py-2">
                  No categories match "{globalSearch}".
                </div>
              )}
            </div>
          </div>

          {/* PRICE */}
          <div>
            <SectionHeader
              icon={IndianRupee}
              label={`Price (${currencyCode})`}
              tint="saffron"
              count={priceDirty ? 1 : 0}
              hint="Drag either handle to set a price band. Colours reflect low → high buying value."
              testId="shop-filter-section-price"
            />
            <div className="px-3 pb-4">
              <GradientRangeSlider
                min={0}
                max={200000}
                step={500}
                value={priceRange}
                onChange={onSetPriceRange}
                formatValue={(v) => formatPrice(v, { hideDecimals: true })}
                testIdPrefix="shop-filter-price"
                label=""
              />
            </div>
          </div>

          {/* BRAND */}
          <div>
            <SectionHeader
              icon={Building2}
              label="Brand"
              tint="fuchsia"
              count={brandCount}
              hint="Pick a brand to filter. Search across the full brand catalogue above."
              testId="shop-filter-section-brand"
            />
            <div className="px-1 pb-3 space-y-0.5 pr-1" data-testid="shop-filter-brand-list">
              {filteredBrands.length === 0 ? (
                <div className="text-[11px] text-[color:var(--sg-muted)] px-3 py-2">
                  {q ? `No brands match "${globalSearch}".` : "No brands available yet."}
                </div>
              ) : filteredBrands.slice(0, 40).map((b) => (
                <FilterRow
                  key={b}
                  active={brand === b}
                  onClick={() => onSetBrand(brand === b ? null : b)}
                  label={b}
                  testId={`shop-filter-brand-${b}`}
                />
              ))}
              {filteredBrands.length > 40 && (
                <div className="text-[10px] text-[color:var(--sg-muted)] px-3 pt-2 flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  {filteredBrands.length - 40} more — refine with search above.
                </div>
              )}
            </div>
          </div>

          {/* SIBASCORE */}
          <div>
            <SectionHeader
              icon={Star}
              label="SibaScore"
              tint="emerald"
              count={scoreDirty ? 1 : 0}
              hint="0-1000 trust score, weighted across 11 signals (sentiment, reviews, brand history, etc)."
              testId="shop-filter-section-score"
            />
            <div className="px-3 pb-4">
              <GradientRangeSlider
                min={0}
                max={1000}
                step={10}
                value={scoreRange}
                onChange={onSetScoreRange}
                formatValue={(v) => String(v)}
                testIdPrefix="shop-filter-score"
                label=""
              />
            </div>
          </div>

          {/* CONDITION */}
          <div>
            <SectionHeader
              icon={Sparkles}
              label="Condition"
              tint="slate"
              count={conditionDirty ? 1 : 0}
              hint="Filter by item condition. Refurbished items ship with the seller's warranty."
              testId="shop-filter-section-condition"
            />
            <div className="px-1 pb-4">
              <div className="grid grid-cols-2 gap-1.5">
                {([
                  { k: "any", l: "Any" },
                  { k: "new", l: "New" },
                  { k: "used", l: "Used" },
                  { k: "refurbished", l: "Refurbished" },
                ] as { k: ConditionKey; l: string }[]).map((c) => {
                  const active = condition === c.k;
                  return (
                    <button
                      key={c.k}
                      onClick={() => onSetCondition(c.k)}
                      data-testid={`shop-filter-condition-${c.k}`}
                      className={`text-[12px] px-2 py-1.5 rounded-md font-display font-medium transition-all border ${
                        active
                          ? "bg-[color:var(--sb-saffron,#F97316)] text-white border-[color:var(--sb-saffron,#F97316)] shadow-sm"
                          : "bg-[color:var(--sg-card)] text-[color:var(--sg-fg)] border-[color:var(--sg-border)] hover:border-[color:var(--sb-saffron,#F97316)]/60 hover:text-[color:var(--sb-saffron,#F97316)]"
                      }`}
                    >
                      {c.l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer — keyboard-hint strip */}
        <div className="hidden md:flex shrink-0 items-center gap-3 px-4 py-2 border-t border-[color:var(--sg-border)] text-[10px] text-[color:var(--sg-muted)] bg-[color:var(--sg-card)]">
          <span className="inline-flex items-center gap-1">
            <kbd className="text-[9px] font-mono bg-[color:var(--sg-muted-bg)] border border-[color:var(--sg-border)] rounded px-1 py-0.5">
              {isMac ? "⌘F" : "Ctrl+F"}
            </kbd>
            <span>focus search</span>
          </span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <kbd className="text-[9px] font-mono bg-[color:var(--sg-muted-bg)] border border-[color:var(--sg-border)] rounded px-1 py-0.5">
              ⇧C
            </kbd>
            <span>clear</span>
          </span>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ShopFilterPanel;
